/**
 * TransactionPhaseBar — Horizontal 4-phase progress bar for the transaction lifecycle.
 *
 * Phases: Negotiation → Documents → Contingencies → Closing
 *
 * @param {Object}   props
 * @param {string}   props.currentPhase    - 'negotiation' | 'documents' | 'contingencies' | 'closing'
 * @param {string[]} props.completedPhases - e.g. ['negotiation']
 */

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import './TransactionPhaseBar.css';

const PHASES = [
  { key: 'negotiation',    label: 'Negotiation',    prevLabel: null },
  { key: 'documents',      label: 'Documents',      prevLabel: 'Negotiation' },
  { key: 'contingencies',  label: 'Contingencies',  prevLabel: 'Documents' },
  { key: 'closing',        label: 'Closing',        prevLabel: 'Contingencies' },
];

const PHASE_ORDER = PHASES.map((p) => p.key);

/**
 * Derive index of a phase key (-1 if unknown).
 */
const phaseIndex = (key) => PHASE_ORDER.indexOf(key);

const CheckIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M2.5 7L5.5 10L11.5 4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LockIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 13 13"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <rect x="2" y="5.5" width="9" height="6.5" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M4.5 5.5V3.5a2 2 0 014 0v2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const TransactionPhaseBar = ({ currentPhase, completedPhases }) => {
  const safeCurrentPhase = currentPhase || 'negotiation';
  const safeCompleted = Array.isArray(completedPhases) ? completedPhases : [];

  const currentIdx = phaseIndex(safeCurrentPhase);
  const connectorRef = useRef([]);

  // Trigger the connector fill animation on mount for each completed/active segment
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    if (prefersReducedMotion) return;

    connectorRef.current.forEach((el, i) => {
      if (!el) return;
      const fill = el.querySelector('.tx-phase-bar__connector-fill');
      if (!fill) return;
      // Segment i connects phase[i] to phase[i+1].
      // Fill if phase[i] is completed (i.e. index < current).
      if (i < currentIdx) {
        // Delay slightly per segment for stagger
        fill.style.transitionDelay = `${i * 80}ms`;
        // Force reflow then add class
        // eslint-disable-next-line no-unused-expressions
        fill.offsetWidth;
        fill.classList.add('tx-phase-bar__connector-fill--active');
      }
    });
  }, [currentIdx]);

  // Mobile compact bar labels
  const prevPhase = currentIdx > 0 ? PHASES[currentIdx - 1] : null;
  const nextPhase = currentIdx < PHASES.length - 1 ? PHASES[currentIdx + 1] : null;

  return (
    <nav
      className="tx-phase-bar"
      aria-label="Transaction phase progress"
    >
      {/* Desktop bar */}
      {/* role="list" is NOT redundant here: the track sets `list-style: none`,
          and Safari/VoiceOver drops list semantics from a list styled that way.
          The explicit role restores them. See TransactionPhaseBar.css. */}
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
      <ol className="tx-phase-bar__track" role="list">
        {PHASES.map((phase, idx) => {
          const isCompleted = safeCompleted.includes(phase.key) || idx < currentIdx;
          const isActive = phase.key === safeCurrentPhase;
          const isLocked = !isCompleted && !isActive;

          let nodeState = '';
          if (isCompleted) nodeState = 'tx-phase-bar__node--completed';
          else if (isActive) nodeState = 'tx-phase-bar__node--active';
          else nodeState = 'tx-phase-bar__node--locked';

          const tooltipText = isLocked && phase.prevLabel
            ? `Available after ${phase.prevLabel} is complete`
            : null;

          return (
            <React.Fragment key={phase.key}>
              {/* Connector between nodes */}
              {idx > 0 && (
                <li
                  className="tx-phase-bar__connector"
                  aria-hidden="true"
                  ref={(el) => { connectorRef.current[idx - 1] = el; }}
                >
                  <span className="tx-phase-bar__connector-fill" />
                </li>
              )}

              {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
              <li
                className={`tx-phase-bar__node ${nodeState}`}
                role="listitem"
                aria-current={isActive ? 'step' : undefined}
                aria-label={`${phase.label}${isCompleted ? ', completed' : isActive ? ', current step' : ', not yet available'}`}
                title={tooltipText || undefined}
              >
                <span className="tx-phase-bar__circle" aria-hidden="true">
                  {isCompleted && <CheckIcon />}
                  {isActive && <span className="tx-phase-bar__active-dot" />}
                  {isLocked && <LockIcon />}
                </span>
                <span className="tx-phase-bar__label">
                  {phase.label}
                </span>
              </li>
            </React.Fragment>
          );
        })}
      </ol>

      {/* Mobile compact view */}
      <div className="tx-phase-bar__mobile" aria-hidden="true">
        {prevPhase && (
          <span className="tx-phase-bar__mobile-prev">
            ← {prevPhase.label}
          </span>
        )}
        <span className="tx-phase-bar__mobile-current">
          {PHASES[currentIdx]?.label ?? 'Negotiation'}
        </span>
        {nextPhase && (
          <span className="tx-phase-bar__mobile-next">
            {nextPhase.label} →
          </span>
        )}
      </div>
    </nav>
  );
};

TransactionPhaseBar.propTypes = {
  currentPhase: PropTypes.oneOf(['negotiation', 'documents', 'contingencies', 'closing']),
  completedPhases: PropTypes.arrayOf(PropTypes.string),
};

TransactionPhaseBar.defaultProps = {
  currentPhase: 'negotiation',
  completedPhases: [],
};

export default TransactionPhaseBar;
